import {
  Menu,
  LogOut,
  ChevronDown,
  UserCircle,
  Search,
  Languages,
  Check,
  Sparkles,
} from 'lucide-react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { useLanguageStore, type Language } from '../../store/useLanguageStore';
import { useVisibleReleaseNotes } from '../../hooks/useVisibleReleaseNotes';
import { CommandPalette } from './CommandPalette';
import { TimeclockWidget } from './TimeclockWidget';

const LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
];

interface HeaderProps {
  onMenuClick: () => void;
}

const PATH_TO_TITLE: Record<string, string> = {
  '/': 'navigation:menu.dashboard',
  '/open-items': 'navigation:menu.openItems',
  '/customers': 'navigation:menu.customers',
  '/objects': 'navigation:menu.objects',
  '/services': 'navigation:menu.services',
  '/tasks': 'navigation:menu.tasks',
  '/checklist-protocol': 'navigation:menu.checklistProtocol',
  '/my-checklists': 'navigation:menu.myChecklists',
  '/employees': 'navigation:menu.employees',
  '/offers': 'navigation:menu.offers',
  '/invoices': 'navigation:menu.invoices',
  '/planning': 'navigation:menu.planning',
  '/my-plan': 'navigation:menu.myPlan',
  '/absences': 'navigation:menu.absences',
  '/absences/admin': 'navigation:menu.absencesAdmin',
  '/timeclock': 'navigation:menu.timeClock',
  '/timeclock/admin': 'navigation:menu.timeClockAdmin',
  '/work-schedule-models': 'navigation:menu.workScheduleModels',
  '/vehicles': 'navigation:menu.vehicles',
  '/vehicles/logbook': 'navigation:menu.vehicleLogbook',
  '/tenants': 'navigation:menu.tenants',
  '/profile': 'navigation:menu.profile',
  '/release-notes': 'releaseNotes:page.title',
  '/settings': 'navigation:menu.settings',
  '/settings/timeclock-groups': 'navigation:menu.timeClockGroups',
  '/price-indexes/history': 'navigation:menu.priceIndex',
};

function resolveTitleKey(pathname: string): string | null {
  const candidates = Object.keys(PATH_TO_TITLE).sort((a, b) => b.length - a.length);
  for (const path of candidates) {
    if (pathname === path || pathname.startsWith(path + '/')) {
      return PATH_TO_TITLE[path];
    }
  }
  return null;
}

function roleBadgeKey(role: string | undefined | null): string | null {
  if (!role) return null;
  const lower = role.toLowerCase();
  if (lower === 'superadmin') return 'header:roleBadge.superadmin';
  if (lower === 'verwaltung') return 'header:roleBadge.admin';
  if (lower === 'mitarbeiter') return 'header:roleBadge.employee';
  return null;
}

function getInitials(fullName: string | undefined | null): string {
  if (!fullName) return '–';
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '–';
}

export function Header({ onMenuClick }: HeaderProps) {
  const { t } = useTranslation(['header', 'navigation', 'releaseNotes']);
  const { employee, logout, isSuperAdmin, isAdmin } = useAuthStore();
  const { hasUnseen: hasUnseenReleaseNotes } = useVisibleReleaseNotes();
  const { language, setLanguage } = useLanguageStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  const handleGlobalKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setIsCommandPaletteOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const titleKey = useMemo(() => resolveTitleKey(location.pathname), [location.pathname]);
  const pageTitle = titleKey ? t(titleKey) : '';
  const badgeKey = roleBadgeKey(employee?.role);
  const roleLabel = badgeKey ? t(badgeKey) : '';
  const initials = getInitials(employee?.fullName);
  // SuperAdmins werden von SuperAdminBlock aus dem Mandanten-Layout geworfen —
  // ihre Menüziele liegen unter /admin/*.
  const menuPath = (path: string) => (isSuperAdmin ? `/admin${path}` : path);

  return (
    <header className="bg-white border-b border-slate-200 h-14 flex items-center px-4 lg:px-6 sticky top-0 z-10">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-1.5 mr-2 rounded hover:bg-slate-50 text-slate-600"
        aria-label={t('header:menu.open')}
      >
        <Menu className="w-5 h-5" strokeWidth={2} />
      </button>

      <div className="flex items-center gap-3 min-w-0">
        {pageTitle && (
          <h1 className="text-[15px] font-semibold text-slate-900 tracking-tight truncate">
            {pageTitle}
          </h1>
        )}
        {roleLabel && (
          <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            {roleLabel}
          </span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-3">
        <button
          onClick={() => setIsCommandPaletteOpen(true)}
          className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md text-[12px] text-slate-500 transition-colors"
          aria-label={t('header:menu.search')}
        >
          <Search className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
          <span>{t('header:menu.search')}</span>
          <kbd className="ml-2 inline-flex items-center px-1 py-0.5 text-[10px] bg-white border border-slate-200 rounded font-medium text-slate-500">
            {t('header:menu.searchKbd')}
          </kbd>
        </button>

        {/* Language switcher */}
        <div className="relative">
          <button
            onClick={() => {
              setShowLanguageMenu((v) => !v);
              setShowUserMenu(false);
            }}
            aria-label={t('header:menu.languageToggle')}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <Languages className="w-4 h-4 text-slate-500" strokeWidth={2} />
            <span className="font-mono-num tracking-wide">{language.toUpperCase()}</span>
          </button>
          {showLanguageMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowLanguageMenu(false)}
              />
              <div
                className="absolute right-0 mt-2 w-44 bg-white rounded-md border border-slate-200 py-1 z-20"
                style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
              >
                {LANGUAGES.map((lng) => {
                  const isActive = language === lng.code;
                  return (
                    <button
                      key={lng.code}
                      onClick={() => {
                        setLanguage(lng.code);
                        setShowLanguageMenu(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-[13px] flex items-center gap-2 transition-colors ${
                        isActive
                          ? 'bg-emerald-50 text-slate-900 font-medium'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span aria-hidden>{lng.flag}</span>
                      <span className="flex-1">{lng.label}</span>
                      {isActive && (
                        <Check className="w-3.5 h-3.5 text-[#00A871]" strokeWidth={2.5} />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <TimeclockWidget />

        <div className="pl-3 ml-1 border-l border-slate-200 relative">
          <button
            onClick={() => setShowUserMenu((v) => !v)}
            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 text-[13px] text-slate-600"
          >
            <div className="relative">
              <div className="w-7 h-7 bg-[#00A871] rounded-full flex items-center justify-center text-white text-[11px] font-semibold">
                {initials}
              </div>
              {hasUnseenReleaseNotes && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="hidden md:flex flex-col items-start leading-tight">
              <span className="font-medium text-slate-900">{employee?.fullName}</span>
              {roleLabel && <span className="text-[11px] text-slate-500">{roleLabel}</span>}
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
          </button>

          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowUserMenu(false)}
              />
              <div
                className="absolute right-0 mt-2 w-56 bg-white rounded-lg border border-slate-200 py-1 z-20"
                style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
              >
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-[13px] font-medium text-slate-900">
                    {employee?.fullName}
                  </p>
                  {employee?.personalNumber && (
                    <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
                      {employee.personalNumber}
                    </p>
                  )}
                  {employee?.email && (
                    <p className="text-[11px] text-slate-500 truncate">{employee.email}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate(menuPath('/profile'));
                  }}
                  className="w-full px-4 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <UserCircle className="w-4 h-4" strokeWidth={2} />
                  <span>{t('header:menu.profile')}</span>
                </button>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate(menuPath('/release-notes'));
                    }}
                    className="w-full px-4 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" strokeWidth={2} />
                    <span>{t('releaseNotes:menu.label')}</span>
                    {hasUnseenReleaseNotes && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#00A871]" />
                    )}
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-[13px] text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" strokeWidth={2} />
                  <span>{t('header:menu.logout')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {createPortal(
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onClose={() => setIsCommandPaletteOpen(false)}
        />,
        document.body
      )}
    </header>
  );
}
